import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import Label from "../../Label";
import { splitDate } from "../../../utils/tableFunctions";
import { Clear, Edit } from "@mui/icons-material";
import TableView from "../../Table";
import HeaderHelpPage from "../../HeaderHelpPage";
import { styles } from "../../../utils/styles";
import { APP_USER_SESSION } from "../../../constant/AppConstants";
import { StorageUtils } from "../../../utils";
import EditDialog from "../../Admin-Terminal/Dialogs/EditDialog";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { getPaymentHostDetails, getPaymentHostWithActions } from "../../../services/payment";
import PendingDetailsComparison from "../../PendingDetailsComparison";

const parsePaymentHostInfo = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value || "{}") : value;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return value ? { Value: value } : {};
  }
};

const HostDetailsTable = ({ value }) => {
  const entries = Object.entries(parsePaymentHostInfo(value));
  if (!entries.length) return "N/A";

  return (
    <TableContainer>
      <Table size="small" aria-label="Host details" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "40%" }}>Key</TableCell>
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([key, item]) => (
            <TableRow key={key}>
              <TableCell component="th" scope="row" sx={{ overflowWrap: "anywhere", verticalAlign: "top" }}>
                {key}
              </TableCell>
              <TableCell sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap", verticalAlign: "top" }}>
                {item == null ? "N/A" : typeof item === "object" ? JSON.stringify(item, null, 2) : String(item)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PAYMENT_HOST_FIELDS = [
  { key: "name", label: "Payment Host" },
  { key: "payment_brand", label: "Payment Brand" },
  {
    key: "currency",
    label: "Allowed Currencies",
    render: (value) =>
      Array.isArray(value) && value.length
        ? value.map((item) => item.currency).join(", ")
        : "N/A",
  },
  {
    key: "info",
    label: "Host Details",
    render: (value) => <HostDetailsTable value={value} />,
  },
];

const PendingPaymentHostDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [paymentHostDetails, setPaymentHostDetails] = useState();
  const [loaded, setLoaded] = useState();
  const [selectedId, setSelectedId] = useState();
  const [mask, setMask] = useState(true);
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));

  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION)?.role || {};
  const userRoleTerminal = userRole?.terminal || [];

  useEffect(() => {
    if (!detailsData && action) {
      getPaymentHostWithActions(id, setPaymentHostDetails, setLoaded);
      return;
    }

    setPaymentHostDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const handleCloseDialog = () => {
    selectedId && setSelectedId();
  };

  const InfoColumn = () => {
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Payment Host</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.name?.toUpperCase()}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Payment Brand</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.payment_brand?.name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Allow Currency</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {/** TODO: show payment method list as join string  */}
              {paymentHostDetails.currency.length === 0
                ? ""
                : paymentHostDetails.currency
                  .map(({ id, currency }) => `${currency}`)
                  .join(", ")}
            </Grid>

            <Grid item xs={12}>
              {/* <Typography variant="subtitle1">Host details</Typography> */}
              <Button
                sx={{ mx: "auto" }}
                onClick={() => setMask(!mask)}
                endIcon={mask ? <VisibilityOff /> : <Visibility />}
                variant="outlined"
              >
                Reveal Details
              </Button>
            </Grid>
            {paymentHostDetails.info && Object.entries(JSON.parse(paymentHostDetails.info)).map(
              ([key, value]) => (
                <>
                  <Grid item xs={6} md={5}>
                    <Typography
                      variant="subtitle1"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {key}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    <FormControl fullWidth>
                      <TextField
                        hiddenLabel
                        disabled
                        defaultValue={value}
                        variant="filled"
                        size="small"
                        type={mask ? "password" : "text"}
                      />
                    </FormControl>
                  </Grid>
                </>
              )
            )}
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            sx={{ mx: "auto", mt: 5 }}
          >

          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Deleted"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={paymentHostDetails.is_approved ? "success" : "error"}
                >
                  {paymentHostDetails.is_approved ? "YES" : "NO"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={paymentHostDetails.is_active ? "success" : "error"}
                >
                  {paymentHostDetails.is_active ? "NO" : "YES"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.created_at
                ? splitDate(paymentHostDetails.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.created_by
                ? paymentHostDetails.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.approved_at
                ? splitDate(paymentHostDetails.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentHostDetails.approved_by
                ? paymentHostDetails.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {splitDate(paymentHostDetails.updated_at)}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {paymentHostDetails.updated_by
                    ? paymentHostDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="PaymentHost Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {paymentHostDetails.action && paymentHostDetails.action}
        </Typography>
      )}
      {loaded ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
              {isCheckerView && !paymentHostDetails.original_unavailable ? (
                <>
                  <PendingDetailsComparison
                    data={paymentHostDetails}
                    original={paymentHostDetails.original}
                    fields={PAYMENT_HOST_FIELDS}
                  />
                  <DateColumn />
                </>
              ) : (
                <>
                  {action && <DateColumn />}
                  <InfoColumn />
                  {!action && <DateColumn />}
                </>
              )}
            </Grid>
            {paymentHostDetails.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {paymentHostDetails.reject_message}
                </Typography>
              </Typography>
            )}
            {paymentHostDetails.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {paymentHostDetails.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {selectedId &&
        <EditDialog
          open={true}
          onClose={handleCloseDialog}
          selectedId={selectedId}
        />
      }
    </Container>
  );
};

export default PendingPaymentHostDetails;
